import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Copy, Share2, CheckCircle2, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '../services/apiService';

const ReferralLinks = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    loadReferralData();
  }, [navigate]);

  const loadReferralData = async () => {
    try {
      const data = await userAPI.getReferralLink();
      setReferralData(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Failed to load referral link');
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    if (referralData?.referralLink) {
      navigator.clipboard.writeText(referralData.referralLink);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = async () => {
    if (navigator.share && referralData?.referralLink) {
      try {
        await navigator.share({
          title: 'Join Netix Fibernet',
          text: 'Get high-speed internet and security solutions with my referral link!',
          url: referralData.referralLink
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      toast.error('Share not supported on this browser');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || !referralData) return null;

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Your Referral Link</h1>
          <p className="text-gray-700 text-lg font-medium">Share your unique link and start earning</p>
        </div>

        {/* Main Referral Link Card */}
        <Card className="border-4 border-red-400 mb-8 shadow-2xl bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl">Primary Referral Link</CardTitle>
            <CardDescription>Your unique link to share anywhere</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex gap-3">
              <Input 
                value={referralData.referralLink || ''} 
                readOnly 
                className="h-14 text-lg font-mono bg-gray-50"
              />
              <Button 
                onClick={handleCopy}
                className="h-14 px-6 text-white border-2 border-yellow-400 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
              >
                {copied ? (
                  <CheckCircle2 className="h-5 w-5" />
                ) : (
                  <Copy className="h-5 w-5" />
                )}
              </Button>
              <Button 
                onClick={handleShare}
                className="h-14 px-6 text-white border-2 border-pink-400 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)' }}
              >
                <Share2 className="h-5 w-5" />
              </Button>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-yellow-50 border-3 border-yellow-400 rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Your Referral Code</h3>
              <p className="text-3xl font-bold text-red-600">{user.referralCode}</p>
            </div>

            {/* Place Order Button */}
            <div className="pt-4 border-t-2 border-gray-200">
              <Button
                onClick={() => navigate('/place-order')}
                className="w-full h-14 text-lg font-bold text-white border-2 border-green-400 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)' }}
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Place Order for Customer
              </Button>
              <p className="text-sm text-gray-600 mt-2 text-center">
                Submit customer order details to earn commission
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Best Practices */}
        <Card className="mb-8 border-3 border-pink-300 shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Best Practices for Sharing</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)'
                }}>
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Add a personal note</p>
                  <p className="text-gray-600 text-sm">Share why you like Netix Fibernet's products and how they've helped you.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)'
                }}>
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Use in your bio</p>
                  <p className="text-gray-600 text-sm">Add to Instagram, Twitter, or LinkedIn bio for high-traffic visibility.</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{
                  background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)'
                }}>
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Share authentic experiences</p>
                  <p className="text-gray-600 text-sm">Create posts about how you use the products — authenticity converts best.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Example Messages */}
        <Card className="border-3 border-yellow-300 shadow-lg bg-white/90 backdrop-blur-sm">
          <CardHeader>
            <CardTitle>Example Messages</CardTitle>
            <CardDescription>Copy and customize these for quick sharing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-semibold text-gray-900 mb-2">WhatsApp / DM Template:</p>
              <p className="text-gray-700 italic">
                "Hey! I've been using Netix Fibernet for internet services and thought of you. 
                Use my link for more info: {user.referralLink}. If you sign up, I'll earn a small commission — thanks for the support!"
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-semibold text-gray-900 mb-2">Social Media Post:</p>
              <p className="text-gray-700 italic">
                "Loving the fast and reliable internet from @NetixFibernet! 
                If you're looking for top-notch internet services, check them out: {user.referralLink}"
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReferralLinks;
